// ./src/components/features/EventFeatures/PremiumFeaturesCard.tsx
//
// "Premium features in this event" — what stops an event being published, and the two ways out (ROADMAP A2b #62
// P6c, owner 2026-09-29: "they could not publish until the upgrade, or remove premium features easily").
//
// Lists each premium feature the event uses that the account's plan lacks (the caller passes them — see
// `premiumFeaturesBlocking`), with the plans that include it and a Remove each, plus Remove all. The upgrade offer
// follows the brand: eventsmagic sells SUBSCRIPTIONS (→ the Subscription page); PartyLabz upgrades ONE EVENT
// (`eventUpgradeOffer` — a placeholder until event upgrades are sold, ROADMAP A9.4).
//
// ⚠ Removing a feature changes ONLY `settings.features.on` — re-read and merged, never written bare (the column is
//   replaced whole on update). Rows already made stay, and a page with data stays in the menu (#63).
// ⚠ The server refuses the same publish (`EventCore.premiumPublishRefusal`); this card is what lets the host fix it
//   before pressing Publish rather than after.
// ⓘ What the host chose is recorded twice: the event's own `settings.features` (durable, server-side), and analytics
//   events for the funnel — `premium_gate_shown`, `premium_feature_removed`, `premium_upgrade_clicked`.

import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BRAND_ID } from '@/config/branding';
import { eventFeatureDef, eventFeaturesOn } from '@/config/eventSetup.generated';
import { useApp } from '@/contexts';
import { useFeatureGate } from '@/hooks';
import { Event, EventService } from '@/utils/api';
import { analytics, PLANS_SOURCE } from '@/utils/analytics';
import { joinPlanNames, plansIncludingFeature, PlanLike } from '@/utils/eventFeaturePlans';
import { eventUpgradeOffer, subscriptionOffer } from '@/utils/planOffers';
import { AppIcon, ErrorAlert } from '@/components/generic';
import { withFeatures } from '@/components/features/CreateEventWizard/wizardSetup';

import { EventPlansDrawer } from '../Events/EventPlansDrawer';

export interface PremiumFeaturesCardProps {
  event: Event;
  /** The feature codes blocking the publish, in catalog order (never empty when rendered). */
  blocking: string[];
  /** The account's plans, to name the ones that include each feature. */
  plans: readonly PlanLike[];
  /** The event after a removal — the caller re-derives what still blocks. */
  onEventChange: (event: Event) => void;
  /** Where this is shown, for analytics ("wizard_finish"). */
  source: string;
}

export const PremiumFeaturesCard: React.FC<PremiumFeaturesCardProps> = ({ event, blocking, plans, onEventChange, source }) => {
  const { t } = useTranslation(['events', 'common']);
  const navigate = useNavigate();
  const { user } = useApp();
  const { goToUpgrade } = useFeatureGate();

  // Which removal is in flight: a feature code, or 'all'.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<OpResult | null>(null);
  // ⓘ Local state, not a URL stage (ROADMAP #120): this card sits on the create wizard's LAST screen, which is not a
  // routed page — its state lives in the wizard, and a stage URL would drop it. The drawer is a read-only catalog with
  // nothing to restore. The event dashboard's copy of it IS a stage (`/events/:eventId/plans`).
  const [plansOpen, setPlansOpen] = useState(false);

  const currentPlan = user?.account?.currentPlan;
  // ⚠ The copy says "Upgrade, or remove" — so an upgrade path is ALWAYS on screen. PartyLabz upgrades one event; every
  //   other case goes to the plans, including a PAID plan that lacks the feature (a higher plan has it) — which is why
  //   this is not gated on `subscriptionOffer`, which hides for anyone already paying.
  const eventOffer = eventUpgradeOffer(currentPlan, BRAND_ID) === 'offer';
  const subscribeLabel =
    subscriptionOffer(currentPlan) === 'offer'
      ? t('events:premium.subscribe', { defaultValue: 'See plans' })
      : t('events:premium.changePlan', { defaultValue: 'Change plan' });

  // Once per distinct set shown — what a host is stopped by is the funnel's most useful number.
  const shownRef = useRef<string>('');

  useEffect(() => {
    const key = blocking.join(',');

    if (key && shownRef.current !== key) {
      shownRef.current = key;
      analytics.sendEvent('premium_gate_shown', { event_features: key, source, brand: BRAND_ID });
    }
  }, [blocking, source]);

  const remove = async (codes: string[]) => {
    if (!event.id || busy) {
      return;
    }

    setBusy(codes.length > 1 ? 'all' : codes[0]);
    setError(null);

    const current = await EventService.get(event.id);

    if (!current.didSucceed()) {
      setBusy(null);
      setError(current);

      return;
    }

    const fresh = (current.getDataFirst() as Event | undefined) ?? event;
    const settings = withFeatures(fresh.settings ?? null, eventFeaturesOn(fresh.settings ?? null).filter((code) => !codes.includes(code)));
    const res = await EventService.update({ id: event.id }, { settings });

    setBusy(null);

    if (!res.didSucceed()) {
      setError(res);

      return;
    }

    for (const code of codes) {
      analytics.sendEvent('premium_feature_removed', { event_feature: code, source });
    }

    const saved = res.getDataFirst() as Event | undefined;

    onEventChange(saved?.settings ? { ...fresh, ...saved } : { ...fresh, settings });
  };

  const upgradeEvent = () => {
    analytics.sendEvent('premium_upgrade_clicked', { kind: 'event', brand: BRAND_ID, source });
    setPlansOpen(true);
  };

  const subscribe = () => {
    analytics.sendEvent('premium_upgrade_clicked', { kind: 'subscription', brand: BRAND_ID, source });
    goToUpgrade();
  };

  // ROADMAP #209 — the event may have been upgraded in the drawer: ask the server's own check again on the way out, and
  // stand down when nothing is missing any more (this screen's gates do not see an upgrade bought for its event).
  const [cleared, setCleared] = useState(false);
  const closePlans = async () => {
    setPlansOpen(false);

    if (!event.id) {
      return;
    }

    const checked = await EventService.planCheck(event.id);
    const plan = checked.didSucceed() ? (checked.getDataFirst() as { needsUpgrade?: boolean } | undefined) : undefined;

    if (plan && plan.needsUpgrade === false) {
      setCleared(true);
    }
  };

  const one = blocking.length === 1;

  if (cleared) {
    return null;
  }

  return (
    <Card withBorder radius="md" padding="lg" w="100%">
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ThemeIcon variant="light" color="yellow" size={40} radius="md">
            <AppIcon iconName="Crown" size={20} />
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={600} size="md" ta="left">
              {t('events:premium.title', { defaultValue: 'Premium features in this event' })}
            </Text>
            <Text size="md" c="dimmed" ta="left">
              {one
                ? t('events:premium.bodyOne', {
                    defaultValue: 'Your plan does not include this feature, so the event cannot be published yet. Upgrade, or remove it.',
                  })
                : t('events:premium.bodyMany', {
                    defaultValue: 'Your plan does not include these features, so the event cannot be published yet. Upgrade, or remove them.',
                  })}
            </Text>
          </Stack>
        </Group>

        {error ? <ErrorAlert error={error} title={t('events:premium.removeFailed', { defaultValue: 'Could not remove the feature' })} page="Create event" operation="eventUpdate" /> : null}

        <Stack gap="xs">
          {blocking.map((code) => {
            const def = eventFeatureDef(code);
            const planNames = plansIncludingFeature(plans, def?.planFeatureCode);

            return (
              <Group key={code} justify="space-between" wrap="nowrap" gap="sm">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" size={34} radius="md">
                    <AppIcon iconName={def?.icon ?? 'Sparkles'} size={17} />
                  </ThemeIcon>
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="md" fw={600} ta="left">
                      {def?.name ?? code}
                    </Text>
                    {planNames.length > 0 ? (
                      <Text size="md" c="dimmed" ta="left">
                        {t('events:premium.includedIn', { defaultValue: 'Included in {{plans}}', plans: joinPlanNames(planNames) })}
                      </Text>
                    ) : null}
                  </Stack>
                </Group>
                <Button variant="subtle" color="red" size="md" loading={busy === code} disabled={!!busy && busy !== code} onClick={() => void remove([code])}>
                  {t('common:actions.remove', { defaultValue: 'Remove' })}
                </Button>
              </Group>
            );
          })}
        </Stack>

        <Group gap="sm" wrap="wrap">
          {eventOffer ? (
            <Button size="md" leftSection={<AppIcon iconName="Crown" size={16} />} onClick={upgradeEvent}>
              {t('events:premium.upgradeEvent', { defaultValue: 'Upgrade this event' })}
            </Button>
          ) : null}
          {!eventOffer ? (
            <Button size="md" leftSection={<AppIcon iconName="Crown" size={16} />} onClick={subscribe}>
              {subscribeLabel}
            </Button>
          ) : null}
          {!one ? (
            <Button variant="default" size="md" loading={busy === 'all'} disabled={!!busy && busy !== 'all'} onClick={() => void remove(blocking)}>
              {t('events:premium.removeAll', { defaultValue: 'Remove all' })}
            </Button>
          ) : null}
        </Group>
      </Stack>

      {event.id ? (
        <EventPlansDrawer
          opened={plansOpen}
          onClose={() => void closePlans()}
          eventId={event.id}
          doing="publish"
          offerUpgrade={eventOffer}
          onSubscribe={subscribe}
          onOpenFeatures={() => navigate(`/events/${event.id}/features`)}
          source={PLANS_SOURCE.PUBLISH}
        />
      ) : null}
    </Card>
  );
};
